import * as React from 'react';
import { cn } from '@/lib/utils';

type CardVariant = 'default' | 'section';

/**
 * When a Card is rendered with `variant="section"` it drops its frame on
 * screens `<md` (becomes a borderless, transparent, full-width section) and
 * restores the full frame on `md+`. The variant is shared via context so that
 * the slotted CardHeader / CardContent / CardFooter automatically collapse
 * their horizontal padding to `px-0` on `<md` (and restore `md:px-5`) without
 * each call site repeating the responsive classes.
 *
 * The default Card (no `variant`) is unchanged.
 */
const CardVariantContext = React.createContext<CardVariant>('default');

const SECTION_ROOT =
  // На телефоне секции идут друг за другом на белом (страница красит подложку
  // в bg-card, см. AnalyticsPage): без рамок серая подложка «съедала» карточки
  // (Алексей 17.09). Между секциями — тонкая черта, первой — не нужна.
  'rounded-none border-0 bg-transparent shadow-none max-md:border-t max-md:border-border/60 max-md:first:border-t-0 ' +
  'md:rounded-xl md:border md:border-border md:bg-card md:shadow-[0_1px_2px_rgba(15,23,42,0.04)]';
const DEFAULT_ROOT =
  'rounded-xl border border-border bg-card shadow-[0_1px_2px_rgba(15,23,42,0.04)]';

// horizontal padding helpers (vertical padding stays on the slot itself)
const SECTION_PX = 'px-0 md:px-5';
const DEFAULT_PX = 'px-5';

export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
}

export const Card = React.forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = 'default', ...props }, ref) => (
    <CardVariantContext.Provider value={variant}>
      <div
        ref={ref}
        data-card=""
        data-card-variant={variant}
        className={cn(
          'text-card-foreground',
          variant === 'section' ? SECTION_ROOT : DEFAULT_ROOT,
          className
        )}
        {...props}
      />
    </CardVariantContext.Provider>
  )
);
Card.displayName = 'Card';

export const CardHeader = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const variant = React.useContext(CardVariantContext);
    return (
      <div
        ref={ref}
        data-card-slot=""
        className={cn(
          // Карточка из одной шапки (функция выключена — содержимого нет): низ без отступа
          // прилипал к рамке (Алексей 27.10 #2989, «Приход к открытию»). last: — только когда шапка последняя.
          'flex flex-col gap-1.5 pt-5 last:pb-5',
          variant === 'section' ? SECTION_PX : DEFAULT_PX,
          className
        )}
        {...props}
      />
    );
  }
);
CardHeader.displayName = 'CardHeader';

export const CardTitle = React.forwardRef<HTMLHeadingElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, ...props }, ref) => (
    <h3 ref={ref} className={cn('text-base font-semibold leading-tight', className)} {...props} />
  )
);
CardTitle.displayName = 'CardTitle';

export const CardDescription = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLParagraphElement>>(
  ({ className, ...props }, ref) => (
    <p ref={ref} className={cn('text-sm text-muted-foreground', className)} {...props} />
  )
);
CardDescription.displayName = 'CardDescription';

export const CardContent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const variant = React.useContext(CardVariantContext);
    return (
      <div
        ref={ref}
        data-card-slot=""
        className={cn('py-4', variant === 'section' ? SECTION_PX : DEFAULT_PX, className)}
        {...props}
      />
    );
  }
);
CardContent.displayName = 'CardContent';

export const CardFooter = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ className, ...props }, ref) => {
    const variant = React.useContext(CardVariantContext);
    return (
      <div
        ref={ref}
        data-card-slot=""
        className={cn(
          'flex items-center gap-2 pb-5',
          variant === 'section' ? SECTION_PX : DEFAULT_PX,
          className
        )}
        {...props}
      />
    );
  }
);
CardFooter.displayName = 'CardFooter';
